'use client';

import TrySeozastCTA from '@/app/components/TrySeozastCTA';
import Image from 'next/image';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { githubDark } from '@uiw/codemirror-theme-github';
import Head from 'next/head';

const Page = () => {
    const canonicalUrl = 'https://seozast.com/articles/how-to-submit-urls-to-bing-with-indexnow';

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Head con canonical */}
            <Head>
                <title>AI SEO in 2025: How to Make Your Content AI-Citation Ready</title>
                <link rel="canonical" href={canonicalUrl} />
            </Head>
            <section className="mb-16">
                <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4 tracking-tight">
                    How to Submit URLs to Bing with IndexNow (and Why It Matters)
                </h1>
                <div className="text-sm text-neutral-500 mb-8 flex items-center space-x-3">
                    <time dateTime="2025-07-04">July 4, 2025</time>
                    <span className="text-neutral-300">•</span>
                    <span>By SEOzast Team</span>
                </div>

                <div className="relative w-full h-[300px] md:h-[450px] mb-10 rounded-xl overflow-hidden shadow-xl">
                    <Image
                        src="https://images.pexels.com/photos/16125027/pexels-photo-16125027.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"
                        alt="Found by AI"
                        fill
                        className="object-cover"
                        priority
                    />
                    <figcaption className="absolute bottom-2 right-2 text-xs text-white bg-black/50 px-2 py-1 rounded">
                        Photo by Sanket Mishra / Pexels
                    </figcaption>
                </div>

                <div className="article-content space-y-10 text-neutral-800 leading-relaxed font-sans">
                    <p>
                        If you're relying on organic traffic, you want your content indexed fast. That’s where <strong>IndexNow</strong> comes in — a game-changing protocol supported by Bing, Yandex, and other search engines to help your new or updated content appear faster in search results.
                    </p>

                    <h2 className="text-2xl font-semibold text-neutral-900">What is IndexNow?</h2>
                    <p>
                        IndexNow is a simple protocol that lets you notify search engines when content is added, updated, or deleted — instantly. Instead of waiting days (or weeks) for crawlers to rediscover changes, you can tell them directly.
                    </p>

                    <h2 className="text-2xl font-semibold text-neutral-900">Why IndexNow Matters in 2025</h2>
                    <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Instant indexing:</strong> New content appears in Bing results within minutes.</li>
                        <li><strong>Better control:</strong> Tell search engines what’s changed and when.</li>
                        <li><strong>Save crawl budget:</strong> No need for bots to waste resources crawling unchanged pages.</li>
                    </ul>

                    <h2 className="text-2xl font-semibold text-neutral-900">How to Submit a URL to Bing with IndexNow</h2>
                    <p>
                        Submitting URLs is easy. You just need your website’s API key (more on that below), then make a simple request. Here’s an example using JavaScript:
                    </p>

                    <pre className="bg-neutral-100 text-sm p-4 rounded overflow-auto">
                        <code>
                            fetch("https://www.bing.com/indexnow?url=https://yourdomain.com/updated-page&key=YOUR_API_KEY")
                        </code>
                    </pre>

                    <h3 className="text-xl font-semibold text-neutral-800">Using cURL instead?</h3>
                    <pre className="bg-neutral-100 text-sm p-4 rounded overflow-auto">
                        <code>
                            curl "https://www.bing.com/indexnow?url=https://yourdomain.com/updated-page&amp;key=YOUR_API_KEY"
                        </code>
                    </pre>

                    <h2 className="text-2xl font-semibold text-neutral-900">Where to Get Your API Key</h2>
                    <p>
                        1. Create a text file named <code>key.txt</code> with your API key inside.<br />
                        2. Upload it to the root of your domain, e.g. <code>https://yourdomain.com/key.txt</code><br />
                        3. Make sure it's publicly accessible. This proves domain ownership.
                    </p>

                    <h2 className="text-2xl font-semibold text-neutral-900">When to Use IndexNow</h2>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>You publish a new blog post or product page</li>
                        <li>You update key content (title, structure, metadata)</li>
                        <li>You delete or redirect a page</li>
                    </ul>

                    <h2 className="text-2xl font-semibold text-neutral-900">IndexNow and SEOzast</h2>
                    <p>
                        If you're using <a href="https://seozast.com" className="text-emerald-600 hover:underline">SEOzast</a>, good news: all articles you generate come with an auto-submit option to IndexNow (via fetch or webhook). That means:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>No delays waiting for indexing</li>
                        <li>No need to build your own integration</li>
                        <li>Peace of mind that your content is visible fast</li>
                    </ul>

                    <h2 className="text-2xl font-semibold text-neutral-900">Conclusion</h2>
                    <p>
                        In a search landscape dominated by speed and AI, <strong>IndexNow</strong> is the easiest way to make sure your content doesn’t get left behind. Whether you use it manually or with a tool like SEOzast, it's a must-have in your SEO toolkit.
                    </p>
                    <div className="bg-neutral-100 p-8 rounded-lg shadow-sm text-center mt-10">
                        <h3 className="text-lg font-semibold mb-4">Want Your Pages Indexed Instantly?</h3>
                        <TrySeozastCTA />
                    </div>

                    <div className="mt-16">
                        <h2 className="text-2xl font-semibold mb-6 text-neutral-900">Related Articles</h2>
                        <ul className="space-y-4 text-neutral-700 list-disc pl-6">
                            <li>
                                <a
                                    href="/articles/get-found-in-chatgpt-and-perplexity"
                                    className="text-emerald-600 hover:underline font-medium"
                                >
                                    Get Found in ChatGPT and Perplexity
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/articles/what-is-indexnow-and-how-it-helps-content-show-up-in-chatgpt"
                                    className="text-emerald-600 hover:underline font-medium"
                                >
                                    What is IndexNow and How It Helps Your Content Show Up in ChatGPT
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/articles/geo-vs-seo"
                                    className="text-emerald-600 hover:underline font-medium"
                                >
                                    GEO vs. SEO: Mastering Search in the AI Era of 2025
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Page;
